import React from 'react';
import { useField, FieldProps } from 'react-final-form';
import { Trans } from '@lingui/react';
import {
  SecondaryButton,
  SecondaryGhostButton,
  CustomCheckbox,
} from '@qover/molecules';
import { FormattedCurrency } from '@qover/react-intl';
import { Body, Caption, Heading4 } from '@qover/typography';
import { Container } from './optionCard.styles';

type OptionCard = React.InputHTMLAttributes<any> & {
  active?: boolean;
  bestSeller?: boolean;
  'data-test': string;
  description: React.ReactNode;
  heading: React.ReactNode;
  icon?: React.ReactElement;
  invalid?: boolean;
  paymentRythmCaption: React.ReactNode;
  price: number;
  renderWhenActif?: React.ReactNode;
  valid?: boolean;
};

type OptionCardFieldProps = FieldProps<any, any> & OptionCard;

export function OptionCard(props: React.PropsWithChildren<OptionCard>) {
  const { active, invalid, valid, ...inputProps } = props;
  return (
    <Container className={props.checked ? 'selected' : undefined}>
      <div className="content">
        <div className="icon-text">
          {props.icon && <div className="icon">{props.icon}</div>}
          <div className="text">
            <Heading4 as="div">{props.heading}</Heading4>
            <Body as="div" style={{ lineHeight: 1.4 }}>
              {props.description}
            </Body>
          </div>
        </div>
        <div className="action">
          <CustomCheckbox
            {...inputProps}
            id={props.id}
            name={props.name}
            value={props.value}
            active={active}
            invalid={invalid}
            valid={valid}
          >
            {props.checked ? (
              <SecondaryGhostButton
                as="div"
                className="add-option-button"
                role="button"
              >
                <Trans id="general.removeOption" defaults="del option" />
              </SecondaryGhostButton>
            ) : (
              <SecondaryButton as="div" className="add-option-button">
                <Trans id="general.addOption" defaults="add option" />
              </SecondaryButton>
            )}
          </CustomCheckbox>
          <div className="price-container">
            <Heading4
              as="span"
              className="price"
              data-test={`${props['data-test']}.price`}
            >
              <FormattedCurrency value={props.price} />
            </Heading4>
            <Caption className="insured-number">
              {props.paymentRythmCaption}
            </Caption>
          </div>
        </div>
      </div>
      {props.checked && props.renderWhenActif}
      <div className="question-container">{props.children}</div>
    </Container>
  );
}

export function OptionCardField(props: OptionCardFieldProps) {
  const { input, meta } = useField(props.name, { type: 'checkbox' });
  return (
    <OptionCard
      {...props}
      {...input}
      active={meta.active}
      invalid={meta.touched && meta.invalid}
      valid={meta.valid}
    />
  );
}
